import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { deactivateUserRequest } from "../../../../../../utils/APICalls";
import Loading from "../../../../../../components/loading/loading";
import { useDispatch } from "react-redux";
import { getUserDetailsRequest } from "../../../../../../redux/users/slice";

const styles = {
  closeBtn: {
    position: "absolute",
    right: 12,
    top: 12,
    color: "rgba(0, 0, 0, 0.54)",
  },
  layout: {
    width: 500,
    mt: -2,
  },
  message: {
    color: "rgba(0, 0, 0, 0.87)",
    mb: 0.5,
  },
  actionBtn: {
    ml: 2,
    mr: 1,
  },
};

interface DeactivateUserDialogProps {
  showDialog: boolean;
  closeDialog: () => void;
  id: string;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export default function DeactivateUserDialog({
  showDialog,
  closeDialog,
  id,
  onSuccess,
  onError,
}: DeactivateUserDialogProps) {
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const deactivateUser = async () => {
    setLoading(true);
    try {
      const response = await deactivateUserRequest(id);
      setLoading(false);
      if (response?.status === 204) {
        if (!onSuccess) {
          dispatch(getUserDetailsRequest({ id }));
        }
        closeDialog();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMessage =
          response?.data?.message ||
          "Failed to deactivate user. Please try again.";
        if (onError) {
          onError(errorMessage);
        }
      }
    } catch (error) {
      setLoading(false);
      console.error("Error deactivating user:", error);
      if (onError) {
        onError("Failed to deactivate user. Please try again.");
      }
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={closeDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Deactivate User?</DialogTitle>
      <IconButton aria-label="close" onClick={closeDialog} sx={styles.closeBtn}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={styles.layout}>
        <DialogContentText id="alert-dialog-description">
          <Typography sx={styles.message}>
            Are you sure you want to deactivate this user? The account can be
            reactivated by an admin at any time.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={closeDialog}>
          Cancel
        </Button>
        <Button sx={styles.actionBtn} onClick={deactivateUser} autoFocus>
          Deactivate User
        </Button>
      </DialogActions>
      {loading && <Loading />}
    </Dialog>
  );
}
